import React from 'react'
import clsx from 'clsx'
import { Paper, Typography, Box, makeStyles } from '@material-ui/core'
import DescriptionIcon from '@material-ui/icons/Description'
import Tooltip from '../../../../../control/tooltip/tooltip'
import { SummaryItem } from './DashBoardType'
import useTranslate from '../../../../../i18n/useTranslate'

type SummaryCardProps = {
    title: string
    items: SummaryItem[]
    canFilter?: boolean
    onClickLabel?: (item: SummaryItem) => void
    onClickProcessingText?: (item: SummaryItem) => void
    onClickOverdueText?: (item: SummaryItem) => void
    putValueInCircle?: boolean | ((item: SummaryItem) => boolean)
    /** เปิด/ปิดการคลิกเป็นราย item (ถ้าไม่ส่งมา จะใช้ canFilter กับทุก item เหมือนเดิม) */
    canClickItem?: (item: SummaryItem) => boolean
}

const SummaryCard: React.FC<SummaryCardProps> = ({
    title,
    items,
    onClickProcessingText,
    onClickOverdueText,
    canFilter = true,
    putValueInCircle = false,
    canClickItem,
}) => {
    const classes = useStyles()
    const t = useTranslate()

    const decideCircle = (it: SummaryItem) =>
        typeof putValueInCircle === 'function' ? !!putValueInCircle(it) : !!putValueInCircle

    const decideClickable = (it: SummaryItem) => (canClickItem ? !!canClickItem(it) : canFilter)

    const Star = ({ item }: { item: SummaryItem }) => (
        <Tooltip title={t.get('UM.PROJECT_DASHBOARD.OVERDUE')} placement="top">
            <span>
                <Typography
                    variant="body1"
                    component="span"
                    className={clsx(classes.star, { [classes.clickable]: decideClickable(item) })}
                    onClick={decideClickable(item) ? () => onClickOverdueText?.(item) : undefined}
                >
                    ({item.star}*)
                </Typography>
            </span>
        </Tooltip>
    )

    return (
        <Paper className={classes.card} elevation={0}>
            <Typography variant="h6" className={classes.title}>
                {title}
            </Typography>

            <Box className={classes.content}>
                {items.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" className={classes.empty}>
                        {t.get('LABEL.NO_DATA_FOUND')}
                    </Typography>
                ) : (
                    items.map((item) => {
                        const showCircleValue = decideCircle(item)
                        const hasValue = typeof item.value === 'number' || typeof item.value === 'string'
                        const valueText =
                            typeof item.value === 'number' ? item.value.toLocaleString() : (item.value ?? '')

                        const canClickCircle = decideClickable(item) && showCircleValue && hasValue

                        return (
                            <Box key={item.key} className={classes.row}>
                                <Box
                                    className={clsx(classes.iconBox, { [classes.clickable]: canClickCircle })}
                                    style={{ background: item.color || '#9CA3AF' }}
                                    onClick={canClickCircle ? () => onClickProcessingText?.(item) : undefined}
                                >
                                    {showCircleValue && hasValue ? (
                                        <span className={classes.circleValue}>{valueText}</span>
                                    ) : (
                                        <DescriptionIcon style={{ width: 28, height: 28 }} />
                                    )}
                                </Box>

                                <Box className={classes.textCol}>
                                    <Box className={classes.labelRow}>
                                        <Typography variant="body2" className={classes.label}>
                                            {item.name ?? '-'}
                                        </Typography>
                                    </Box>

                                    <Box className={classes.valueRow}>
                                        {!showCircleValue && hasValue && (
                                            <Typography
                                                variant="body1"
                                                className={clsx(classes.value, {
                                                    [classes.clickable]: decideClickable(item),
                                                })}
                                                onClick={
                                                    decideClickable(item)
                                                        ? () => onClickProcessingText?.(item)
                                                        : undefined
                                                }
                                            >
                                                {valueText}
                                            </Typography>
                                        )}
                                        {typeof item.star === 'number' && <Star item={item} />}
                                    </Box>
                                </Box>
                            </Box>
                        )
                    })
                )}
            </Box>
        </Paper>
    )
}

const useStyles = makeStyles((theme) => ({
    card: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        borderRadius: 16,
        padding: theme.spacing(2),
        boxShadow: 'none',
        width: '100%',
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
    },
    title: { textAlign: 'left', fontWeight: 700 },
    content: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '100%',
        flex: 1,
    },
    row: { display: 'flex', alignItems: 'center', marginBottom: theme.spacing(2) },
    iconBox: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(1.5),
        flexShrink: 0,
        color: '#fff',
        transition: 'transform .05s ease, box-shadow .15s ease',
        '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,.08)' },
    },
    circleValue: { fontSize: 18, fontWeight: 800, lineHeight: 1 },
    textCol: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 },
    labelRow: { display: 'flex', alignItems: 'center', gap: 8 },
    valueRow: { display: 'flex', alignItems: 'center', gap: 6 },
    label: { fontWeight: 600 },
    value: { fontWeight: 700 },
    star: { color: '#D62E4B', fontWeight: 700 },
    empty: { paddingBlock: 48, marginInline: 'auto' },
    clickable: { cursor: 'pointer' },
}))

export default SummaryCard
